/**
 * F60b — the pieces of /ops/health (plans/F60b_badges_and_health_ui.md, direction
 * A "Lanes"): the verdict, the four status tiles, and the timeline — four lanes
 * on one shared time axis with one crosshair and one readout.
 *
 * Colour means trouble: healthy volume and latency draw in ink/grey; warning and
 * danger appear only on 4xx/5xx, warn/error logs and breached tiles, always next
 * to an icon or a label.
 */
import { memo, useState, useSyncExternalStore, type ReactNode } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import type { OpsLatencyOverall, OpsRouteLatency } from '@bravemove/types';
import {
  Badge,
  Banner,
  Button,
  Card,
  Chart,
  EmptyState,
  Sparkline,
  Table,
  cn,
  type BadgeTone,
  type ChartSeries,
  type TableColumn,
} from '@bravemove/ui-web';
import { UIIcon, type UIIconName } from '@bravemove/design-tokens/icons';
import {
  errorLogStatus,
  errorPct,
  formatBin,
  formatTick,
  p90Status,
  rateStatus,
  roundMs,
  timeTicks,
  type HealthBin,
  type HealthVerdict,
  type HealthVerdictStatus,
  type TileStatus,
  type TimeZoneMode,
} from './opsHelpers';

type Format = (value: number | null) => string;

// ─── Verdict ─────────────────────────────────────────────────────────────────

const VERDICT_LOOK: Record<
  HealthVerdictStatus,
  { readonly icon: UIIconName; readonly panel: string; readonly mark: string }
> = {
  healthy: { icon: 'check', panel: 'border-hairline bg-surface', mark: 'text-success' },
  quiet: { icon: 'pulse', panel: 'border-hairline bg-surface', mark: 'text-ink-3' },
  degraded: { icon: 'eye', panel: 'border-warning/40 bg-warning/10', mark: 'text-warning' },
  failing: { icon: 'flag', panel: 'border-danger/40 bg-danger/10', mark: 'text-danger' },
};

export function VerdictPanel({
  verdict,
  asOf,
  ms,
}: {
  readonly verdict: HealthVerdict;
  /** Already formatted time of the range end. */
  readonly asOf: string;
  readonly ms: Format;
}) {
  const { t, locale } = useI18n();
  const look = VERDICT_LOOK[verdict.status];
  const minutes = verdict.minutes;

  let reason: string;
  if (verdict.status === 'healthy') {
    reason = t('admin:opsLogs.health.verdict.reasonHealthy', { minutes });
  } else if (verdict.status === 'quiet') {
    reason = t('admin:opsLogs.health.verdict.reasonQuiet', { minutes });
  } else {
    const problems: string[] = [];
    if (verdict.s5xx > 0) {
      problems.push(t('admin:opsLogs.health.verdict.server5xx', { count: verdict.s5xx }));
    }
    if (verdict.errors > 0) {
      problems.push(t('admin:opsLogs.health.verdict.errorLogs', { count: verdict.errors }));
    }
    if (verdict.s5xx === 0 && verdict.errors === 0) {
      problems.push(t('admin:opsLogs.health.verdict.slow', { ms: ms(verdict.p90) }));
    }
    const list = new Intl.ListFormat(locale, { type: 'conjunction' }).format(problems);
    reason = t('admin:opsLogs.health.verdict.reasonProblems', { problems: list, minutes });
  }

  return (
    <section
      data-testid="ops-health-verdict"
      data-status={verdict.status}
      className={cn('flex items-start gap-4 rounded-xl border p-5', look.panel)}
    >
      <span
        aria-hidden
        className={cn(
          'grid size-11 shrink-0 place-items-center rounded-full bg-surface',
          look.mark,
        )}
      >
        <UIIcon name={look.icon} size={22} color="currentColor" />
      </span>
      <div className="min-w-0 space-y-1">
        <h2 className="text-h3 font-semibold text-ink">
          {t(`admin:opsLogs.health.verdict.${verdict.status}`)}
        </h2>
        <p className="text-body text-ink-2">{reason}</p>
        <p className="text-small text-ink-3">
          {t('admin:opsLogs.health.verdict.asOf', { time: asOf })}
        </p>
      </div>
    </section>
  );
}

// ─── Tiles ───────────────────────────────────────────────────────────────────

const TILE_LOOK: Record<TileStatus, { readonly tone: BadgeTone; readonly icon: UIIconName }> = {
  ok: { tone: 'neutral', icon: 'check' },
  watch: { tone: 'warning', icon: 'eye' },
  alert: { tone: 'danger', icon: 'flag' },
};

function HealthTile({
  label,
  value,
  status,
  trend,
  context,
}: {
  readonly label: string;
  readonly value: string;
  readonly status: TileStatus | null;
  readonly trend: readonly number[];
  readonly context: ReactNode;
}) {
  const { t } = useI18n();
  const look = status ? TILE_LOOK[status] : null;
  return (
    <Card className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-small font-medium text-ink-2">{label}</span>
        {look ? (
          <Badge
            tone={look.tone}
            size="sm"
            icon={<UIIcon name={look.icon} size={10} color="currentColor" />}
          >
            {t(`admin:opsLogs.health.tiles.status.${status}`)}
          </Badge>
        ) : null}
      </div>
      <div className="text-h2 font-semibold text-ink">{value}</div>
      {trend.length >= 2 ? (
        <Sparkline
          data={trend}
          width={200}
          height={28}
          colorVar="--color-ink-3"
          className="w-full"
        />
      ) : (
        <div className="h-7" aria-hidden />
      )}
      <div className="text-small text-ink-3">{context}</div>
    </Card>
  );
}

export function HealthTiles({
  bins,
  overall,
  recentErrors,
  rangeMinutes,
  ms,
}: {
  readonly bins: readonly HealthBin[];
  readonly overall: OpsLatencyOverall;
  /** Error log lines inside the verdict window. */
  readonly recentErrors: number;
  /** Minutes covered by the (trimmed) data, for the per-minute rate. */
  readonly rangeMinutes: number;
  readonly ms: Format;
}) {
  const { t, locale } = useI18n();
  const number = (n: number) => n.toLocaleString(locale);
  const rate = errorPct(overall.requests, overall.s5xx);
  const totals = bins.reduce(
    (acc, b) => ({
      errors: acc.errors + b.errors,
      warn: acc.warn + b.warn,
      info: acc.info + b.info,
    }),
    { errors: 0, warn: 0, info: 0 },
  );
  const perMinute = rangeMinutes > 0 ? overall.requests / rangeMinutes : 0;

  return (
    <div
      className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4"
      data-testid="ops-health-kpis"
    >
      <HealthTile
        label={t('admin:opsLogs.health.tiles.requests')}
        value={number(overall.requests)}
        status={null}
        trend={bins.map((b) => b.requests)}
        context={t('admin:opsLogs.health.tiles.perMinute', {
          value: perMinute < 10 ? perMinute.toFixed(1) : number(Math.round(perMinute)),
        })}
      />
      <HealthTile
        label={t('admin:opsLogs.health.tiles.errorRate')}
        value={
          rate === null
            ? t('admin:opsLogs.health.none')
            : t('admin:opsLogs.units.percent', { value: rate.toFixed(2) })
        }
        status={rateStatus(rate)}
        trend={bins.map((b) => b.s5xx)}
        context={t('admin:opsLogs.health.tiles.ofRequests', {
          s5xx: number(overall.s5xx),
          requests: number(overall.requests),
        })}
      />
      <HealthTile
        label={t('admin:opsLogs.health.tiles.p90')}
        value={ms(overall.p90)}
        status={p90Status(overall.p90)}
        trend={bins.flatMap((b) => (b.p90 === null ? [] : [b.p90]))}
        context={t('admin:opsLogs.health.tiles.percentiles', {
          p50: ms(overall.p50),
          p99: ms(overall.p99),
        })}
      />
      <HealthTile
        label={t('admin:opsLogs.health.tiles.errorLogs')}
        value={number(totals.errors)}
        status={errorLogStatus(totals.errors, recentErrors)}
        trend={bins.map((b) => b.errors)}
        context={t('admin:opsLogs.health.tiles.logContext', {
          warn: number(totals.warn),
          info: number(totals.info),
        })}
      />
    </div>
  );
}

// ─── Timeline ────────────────────────────────────────────────────────────────

const SYNC_ID = 'ops-health-lanes';
/** Every lane shares the Y-axis width and margins so the plots line up. */
const Y_AXIS_WIDTH = 48;
const MARGIN = { top: 6, right: 20, bottom: 0, left: 0 };

interface LaneKey {
  readonly label: string;
  readonly className: string;
  readonly line?: boolean;
}

function LaneLabel({ name, keys }: { readonly name: string; readonly keys: readonly LaneKey[] }) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 md:flex-col md:justify-center md:gap-y-1.5">
      <span className="text-small font-semibold text-ink">{name}</span>
      <span className="flex flex-wrap gap-x-3 gap-y-1 text-caption text-ink-3">
        {keys.map((k) => (
          <span key={k.label} className="inline-flex items-center gap-1.5">
            <span
              aria-hidden
              className={cn(
                k.line ? 'h-0.5 w-3 rounded-full' : 'size-2 rounded-[2px]',
                k.className,
              )}
            />
            {k.label}
          </span>
        ))}
      </span>
    </div>
  );
}

/** Phone widths fit about four time labels; wider screens eight. */
const NARROW_QUERY = '(max-width: 639px)';

function subscribeNarrow(onChange: () => void): () => void {
  if (typeof window.matchMedia !== 'function') return () => undefined;
  const mq = window.matchMedia(NARROW_QUERY);
  mq.addEventListener('change', onChange);
  return () => mq.removeEventListener('change', onChange);
}

function isNarrow(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia(NARROW_QUERY).matches;
}

interface LanesProps {
  readonly bins: readonly HealthBin[];
  readonly timeZone: TimeZoneMode;
  readonly locale: string;
  readonly maxLabels: number;
  readonly onActive: (index: number | null) => void;
}

/**
 * The four lanes. Memoised so the hover readout (state in the parent) doesn't
 * redraw the charts on every pointer move.
 */
const Lanes = memo(function Lanes({ bins, timeZone, locale, maxLabels, onActive }: LanesProps) {
  const { t } = useI18n();
  const rows = bins as unknown as readonly Record<string, unknown>[];
  const { ticks, stepMs } = timeTicks(
    bins.map((b) => b.t),
    timeZone,
    maxLabels,
  );
  const compact = new Intl.NumberFormat(locale, { notation: 'compact', maximumFractionDigits: 1 });
  const count = (v: number) => compact.format(v);
  const latency = (v: number) => (v >= 1000 ? `${compact.format(v / 1000)}s` : `${count(v)}ms`);
  const lane = (key: string) => t(`admin:opsLogs.health.lanes.${key}`);
  const shared = {
    data: rows,
    xKey: 't',
    xTicks: ticks,
    syncId: SYNC_ID,
    showTooltip: false,
    onActiveIndexChange: onActive,
    yAxisWidth: Y_AXIS_WIDTH,
    margin: MARGIN,
  } as const;

  const lanes: ReadonlyArray<{
    readonly id: string;
    readonly name: string;
    readonly keys: readonly LaneKey[];
    readonly chart: ReactNode;
  }> = [
    {
      id: 'traffic',
      name: lane('traffic'),
      keys: [{ label: lane('requests'), className: 'bg-muted' }],
      chart: (
        <Chart
          {...shared}
          kind="bar"
          height={80}
          yTickCount={3}
          showXAxis={false}
          valueFormatter={count}
          series={[{ key: 'requests', label: lane('requests'), colorVar: '--color-muted' }]}
        />
      ),
    },
    {
      id: 'failures',
      name: lane('failures'),
      keys: [
        { label: lane('s5xx'), className: 'bg-danger' },
        { label: lane('s4xx'), className: 'bg-warning' },
      ],
      chart: (
        <Chart
          {...shared}
          kind="bar"
          stacked
          height={80}
          yTickCount={3}
          showXAxis={false}
          valueFormatter={count}
          series={[
            { key: 's5xx', label: lane('s5xx'), colorVar: '--color-danger' },
            { key: 's4xx', label: lane('s4xx'), colorVar: '--color-warning' },
          ]}
        />
      ),
    },
    {
      id: 'logs',
      name: lane('logs'),
      keys: [
        { label: lane('errors'), className: 'bg-danger' },
        { label: lane('warn'), className: 'bg-warning' },
      ],
      chart: (
        <Chart
          {...shared}
          kind="bar"
          stacked
          height={80}
          yTickCount={3}
          showXAxis={false}
          valueFormatter={count}
          series={[
            { key: 'errors', label: lane('errors'), colorVar: '--color-danger' },
            { key: 'warn', label: lane('warn'), colorVar: '--color-warning' },
          ]}
        />
      ),
    },
    {
      id: 'latency',
      name: `${lane('latency')} (${t('admin:opsLogs.health.timeline.logScale')})`,
      keys: [
        { label: lane('p50'), className: 'bg-ink-3', line: true },
        { label: lane('p90'), className: 'bg-info', line: true },
        { label: lane('p99'), className: 'bg-muted', line: true },
      ],
      chart: (
        <Chart
          {...shared}
          kind="line"
          height={150}
          yScale="log"
          valueFormatter={latency}
          xTickFormatter={(iso) => formatTick(iso, stepMs, timeZone, locale)}
          series={LATENCY_SERIES.map((s) => ({ ...s, label: lane(s.key) }))}
        />
      ),
    },
  ];

  return (
    <div className="space-y-3">
      {lanes.map((l) => (
        <div
          key={l.id}
          data-testid={`ops-health-lane-${l.id}`}
          className="grid gap-x-4 gap-y-1 md:grid-cols-[9.5rem_minmax(0,1fr)]"
        >
          <LaneLabel name={l.name} keys={l.keys} />
          {l.chart}
        </div>
      ))}
    </div>
  );
});

/** Drawn p99 → p50 → p90 so the emphasised p90 sits on top. */
const LATENCY_SERIES: readonly ChartSeries[] = [
  { key: 'p99', colorVar: '--color-muted' },
  { key: 'p50', colorVar: '--color-ink-3' },
  { key: 'p90', colorVar: '--color-info' },
];

export function HealthTimeline({
  bins,
  binSeconds,
  dataFrom,
  timeZone,
  ms,
}: {
  readonly bins: readonly HealthBin[];
  readonly binSeconds: number;
  /** First bin with data when earlier empty bins were trimmed. */
  readonly dataFrom: string | null;
  readonly timeZone: TimeZoneMode;
  readonly ms: Format;
}) {
  const { t, locale } = useI18n();
  const [active, setActive] = useState<number | null>(null);
  const [showTable, setShowTable] = useState(false);
  const narrow = useSyncExternalStore(subscribeNarrow, isNarrow, () => false);
  const time = (iso: string) => formatBin(iso, binSeconds, timeZone, locale);
  const number = (n: number) => n.toLocaleString(locale);
  const hovered = active === null ? undefined : bins[active];
  const msCell = (v: number | null) => (v === null ? t('admin:opsLogs.health.none') : roundMs(v));

  const binColumns: TableColumn<HealthBin>[] = [
    { key: 't', header: t('admin:opsLogs.health.lanes.time'), cell: (b) => time(b.t) },
    numericColumn('requests', t('admin:opsLogs.health.lanes.requests'), (b) => number(b.requests)),
    numericColumn('s4xx', t('admin:opsLogs.health.lanes.s4xx'), (b) => number(b.s4xx)),
    numericColumn('s5xx', t('admin:opsLogs.health.lanes.s5xx'), (b) => number(b.s5xx)),
    numericColumn('warn', t('admin:opsLogs.health.lanes.warn'), (b) => number(b.warn)),
    numericColumn('errors', t('admin:opsLogs.health.lanes.errors'), (b) => number(b.errors)),
    numericColumn('p50', t('admin:opsLogs.health.columns.p50'), (b) => msCell(b.p50)),
    numericColumn('p90', t('admin:opsLogs.health.columns.p90'), (b) => msCell(b.p90)),
    numericColumn('p99', t('admin:opsLogs.health.columns.p99'), (b) => msCell(b.p99)),
  ];

  return (
    <Card variant="expanded" className="space-y-4" data-testid="ops-health-timeline">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h2 className="text-body font-semibold text-ink">
          {t('admin:opsLogs.health.timeline.title')}
        </h2>
        {dataFrom ? (
          <span className="text-small text-ink-3">
            {t('admin:opsLogs.health.timeline.dataStarts', { time: time(dataFrom) })}
          </span>
        ) : null}
      </div>
      <p className="min-h-5 text-small tabular-nums text-ink-2" data-testid="ops-health-readout">
        {hovered
          ? t('admin:opsLogs.health.timeline.readout', {
              time: time(hovered.t),
              requests: number(hovered.requests),
              s4xx: number(hovered.s4xx),
              s5xx: number(hovered.s5xx),
              warn: number(hovered.warn),
              errors: number(hovered.errors),
              p90: ms(hovered.p90),
            })
          : t('admin:opsLogs.health.timeline.summary')}
      </p>
      {bins.length === 0 ? (
        <EmptyState title={t('admin:opsLogs.health.timeline.empty')} />
      ) : (
        <Lanes
          bins={bins}
          timeZone={timeZone}
          locale={locale}
          maxLabels={narrow ? 4 : 8}
          onActive={setActive}
        />
      )}
      {bins.length > 0 ? (
        <div className="space-y-2">
          <Button
            variant="ghost"
            size="sm"
            aria-expanded={showTable}
            onClick={() => setShowTable((v) => !v)}
          >
            {showTable
              ? t('admin:opsLogs.health.timeline.hideTable')
              : t('admin:opsLogs.health.timeline.showTable')}
          </Button>
          {showTable ? (
            <div className="max-h-80 overflow-auto" data-testid="ops-health-bins-table">
              <Table
                data={[...bins].reverse()}
                columns={binColumns}
                rowKey={(b) => b.t}
                density="compact"
                stickyHeader
                ariaLabel={t('admin:opsLogs.health.timeline.tableAria')}
              />
            </div>
          ) : null}
        </div>
      ) : null}
    </Card>
  );
}

function numericColumn<T>(
  key: string,
  header: string,
  render: (row: T) => ReactNode,
  sortAccessor?: (row: T) => number,
): TableColumn<T> {
  return {
    key,
    header,
    cell: (row) => (
      <span className="whitespace-nowrap text-[12.5px] tabular-nums">{render(row)}</span>
    ),
    align: 'end',
    width: '1%',
    ...(sortAccessor ? { sortable: true, sortAccessor } : {}),
  };
}

// ─── Per-route table ─────────────────────────────────────────────────────────

/** p99 at or above this reads as slow in the route table. */
const SLOW_P99_MS = 1_000;

export function RouteLatencyTable({
  routes,
  truncated,
  onOpen,
}: {
  readonly routes: readonly OpsRouteLatency[];
  readonly truncated: boolean;
  readonly onOpen: (row: OpsRouteLatency) => void;
}) {
  const { t, locale } = useI18n();
  const number = (n: number) => n.toLocaleString(locale);
  const msCell = (v: number | null) => (v === null ? t('admin:opsLogs.health.none') : roundMs(v));
  const col = (key: string) => t(`admin:opsLogs.health.columns.${key}`);

  const columns: TableColumn<OpsRouteLatency>[] = [
    {
      key: 'method',
      header: col('method'),
      cell: (r) => <span className="font-mono text-[12px] text-ink-3">{r.method}</span>,
      sortable: true,
      sortAccessor: (r) => r.method,
      width: '1%',
    },
    {
      key: 'route',
      header: col('route'),
      cell: (r) => (
        <span
          className="block max-w-[28rem] truncate font-mono text-[12.5px] text-ink"
          title={r.route}
        >
          {r.route}
        </span>
      ),
      sortable: true,
      sortAccessor: (r) => r.route,
    },
    numericColumn(
      'requests',
      col('requests'),
      (r) => number(r.requests),
      (r) => r.requests,
    ),
    numericColumn(
      'p50',
      col('p50'),
      (r) => msCell(r.p50),
      (r) => r.p50 ?? -1,
    ),
    numericColumn(
      'p90',
      col('p90'),
      (r) => msCell(r.p90),
      (r) => r.p90 ?? -1,
    ),
    numericColumn(
      'p99',
      col('p99'),
      (r) =>
        r.p99 !== null && r.p99 >= SLOW_P99_MS ? (
          <span className="text-warning">{msCell(r.p99)}</span>
        ) : (
          msCell(r.p99)
        ),
      (r) => r.p99 ?? -1,
    ),
    numericColumn(
      'max',
      col('max'),
      (r) => msCell(r.max),
      (r) => r.max ?? -1,
    ),
    numericColumn(
      's4xx',
      col('s4xx'),
      (r) => number(r.s4xx),
      (r) => r.s4xx,
    ),
    numericColumn(
      's5xx',
      col('s5xx'),
      (r) =>
        r.s5xx > 0 ? <span className="font-semibold text-danger">{number(r.s5xx)}</span> : '0',
      (r) => r.s5xx,
    ),
    numericColumn(
      'errorPct',
      col('errorPct'),
      (r) => {
        const pct = errorPct(r.requests, r.s5xx);
        if (pct === null) return t('admin:opsLogs.health.none');
        return pct > 0 ? (
          <span className="font-semibold text-danger">{pct.toFixed(1)}</span>
        ) : (
          pct.toFixed(1)
        );
      },
      (r) => errorPct(r.requests, r.s5xx) ?? -1,
    ),
  ];

  return (
    <section className="space-y-2" data-testid="ops-health-routes">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-body font-semibold text-ink">
          {t('admin:opsLogs.health.routesTitle')}
        </h2>
        <span className="text-small text-ink-3">{t('admin:opsLogs.health.routesHint')}</span>
      </div>
      {truncated ? <Banner tone="info">{t('admin:opsLogs.health.routesTruncated')}</Banner> : null}
      <Table
        data={routes}
        columns={columns}
        rowKey={(r) => `${r.method} ${r.route}`}
        density="compact"
        onRowPress={onOpen}
        ariaLabel={t('admin:opsLogs.health.routesTitle')}
        empty={
          <p className="px-3 py-6 text-center text-small text-ink-3">
            {t('admin:opsLogs.health.routesEmpty')}
          </p>
        }
      />
    </section>
  );
}
